<template>
  <div
    class="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-600 dark:bg-slate-800"
  >
    <div class="flex shrink-0 flex-row flex-wrap items-start gap-2">
      <div class="flex min-w-0 flex-1 flex-col">
        <div class="font-bold capitalize-first">{{ date }}</div>
        <div class="text-xs leading-none font-semibold">
          # {{ $t(`enums.activityLog.type.${activity.description}`) }}
        </div>
      </div>
      <div class="flex shrink-0 flex-row items-center gap-2">
        <span class="font-semibold">{{ causerName }}</span>
        <UserAvatar :name="causerName" size="small" flat />
      </div>
    </div>
    <div
      v-if="properties.length"
      class="overflow-hidden rounded border border-slate-200 dark:border-slate-600"
    >
      <table class="w-full text-sm">
        <tr
          v-for="property in properties"
          :key="property.attribute"
          class="odd:bg-gray-100 dark:odd:bg-slate-700"
        >
          <td class="w-1/3 py-1.5 pr-2 pl-3 font-bold wrap-anywhere">
            {{ attributeLabel(property.attribute) }}
          </td>
          <td class="w-1/3 py-1.5 pr-2 wrap-anywhere">{{ property.old }}</td>
          <td class="w-1/3 py-1.5 pr-2 wrap-anywhere">{{ property.new }}</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import UserAvatar from '@c/Avatar/UserAvatar.vue'

// Spatie logs every column; these say nothing to a user.
const HIDDEN_ATTRIBUTES = ['id', 'uuid', 'slug', 'created_at', 'updated_at']

const empty = (value) =>
  value === null || value === undefined || value === '' || value === '---'

/**
 * One activity, as FM's ActivityBlock: when and what (with how long ago),
 * who, and the changed values (old / new).
 */
export default {
  name: 'ActivityBlock',
  components: { UserAvatar },
  props: {
    activity: { type: Object, required: true }
  },
  computed: {
    date() {
      const date = moment(this.activity.created_at).locale(
        this.$i18n.locale.substring(0, 2)
      )
      return `${date.format('LLLL')} (${date.fromNow()})`
    },
    causerName() {
      return this.activity.causer?.full_name ?? this.$t('global.system')
    },
    properties() {
      const { attributes = {}, old = {} } = this.activity.properties ?? {}
      return Object.keys(attributes)
        .filter((key) => !HIDDEN_ATTRIBUTES.includes(key))
        .map((key) => ({
          attribute: key,
          old: empty(old[key]) ? '---' : this.format(old[key]),
          new: empty(attributes[key]) ? '---' : this.format(attributes[key])
        }))
        .filter((line) => line.old !== line.new)
    }
  },
  methods: {
    // JSON columns come as "attributes.expiration_date": translate the last
    // part, else show it readable.
    attributeLabel(attribute) {
      for (const key of [attribute, attribute.split('.').pop()]) {
        if (this.$te(`attributes.${key}`)) return this.$t(`attributes.${key}`)
      }
      return attribute.split('.').pop().replaceAll('_', ' ')
    },
    format(value) {
      if (Array.isArray(value)) return value.join(', ')
      if (typeof value === 'object') return JSON.stringify(value)
      return value
    }
  }
}
</script>
